import { communicationTemplateApi } from "./api";
import type { Template, TemplateApiResponse } from "./types";
import { toast } from "@shared/hooks/use-toast";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useQueryClient } from "@tanstack/react-query";

interface TemplateFilters {
  page?: number;
  limit?: number;
  remote_utility_id?: number;
  search_data?: string;
  channelType?: string;
  category?: string;
  status?: "draft" | "published";
  tags?: string;
}

export const useFilterCommunicationTemplates = (
  filters: TemplateFilters = {}
) => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.ux.template.templatesFilter(filters);

  const enabled = !!remote_utility_id;

  return useSmartQuery(
    key as unknown[],
    () => {
      const isDraft =
        filters.status === "draft"
          ? true
          : filters.status === "published"
          ? false
          : undefined;

      return communicationTemplateApi.getTemplatesFilter(
        remote_utility_id,
        filters.page || 1,
        filters.limit || 10,
        filters.channelType,
        filters.category,
        isDraft,
        filters.tags,
        filters.search_data
      );
    },
    { enabled } // Only run when ID is ready
  );
};

export const useCreateTemplate = (filters: TemplateFilters = {}) => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();
  return useSmartMutation(
    async ({ template, isDraft }: { template: Template; isDraft: boolean }) => {
      if (!utilityId) {
        throw new Error("Utility ID is missing");
      }

      let subject = template.content.subject?.trim() || "";

      if (!subject) {
        subject = template.templateName?.trim()
          ? `${template.templateName.trim()}`
          : "Email Template";
      }

      const apiPayload = {
        remoteUtilityId: utilityId,
        templateName: template.templateName,
        channelType: template.channelType?.toUpperCase() || "EMAIL",
        templateCategory: template.templateCategory || "General",
        content: {
          body: template.content.body || "",
          subject: subject,
        },
        tags: template.tags || [],
        isDraft: isDraft,
        description: template.description,
      };

      return communicationTemplateApi.createTemplate(apiPayload);
    },
    {
      onSuccess: (data, variables) => {
        toast({
          title: "Success",
          description: `Template created successfully!`,
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.ux.template.templatesFilter(filters),
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "",
          description: error?.message || "Failed to save template as draft",
        });
      },
    }
  );
};

export const useSaveAsTemplate = (filters: TemplateFilters = {}) => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (template: Template) => {
      if (!utilityId) {
        throw new Error("Utility ID is missing");
      }

      let subject = template.content.subject?.trim() || "";

      if (!subject) {
        subject = template.templateName?.trim()
          ? `${template.templateName.trim()}`
          : "Email Template";
      }

      const apiPayload = {
        remoteUtilityId: utilityId,
        templateName: template.templateName,
        channelType: template.channelType?.toUpperCase() || "EMAIL",
        templateCategory: template.templateCategory || "General",
        content: {
          body: template.content.body || "",
          subject: subject,
        },
        tags: template.tags || [],
        isDraft: true, // Always save as draft
      };

      return communicationTemplateApi.createTemplate(apiPayload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.ux.template.templatesFilter(filters),
        });
        toast({
          title: "Success",
          description: "Template saved as draft successfully!",
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "",
          description: error?.message || "Failed to save template as draft",
        });
      },
    }
  );
};

export const useUpdateTemplate = (filters: TemplateFilters = {}) => {
  const queryClient = useQueryClient();
  return useSmartMutation(
    async (template: Template) => {
      if (!template.id) {
        throw new Error("Template ID is missing");
      }

      let subject = template.content.subject?.trim() || "";

      if (!subject) {
        subject = template.templateName?.trim()
          ? `${template.templateName.trim()}`
          : "Email Template";
      }

      const apiPayload = {
        templateName: template.templateName,
        channelType: template.channelType?.toUpperCase() || "EMAIL",
        templateCategory: template.templateCategory || "General",
        content: {
          body: template.content.body || "",
          subject: subject,
        },
        tags: template.tags || [],
        isDraft: template.isDraft || false,
      };

      return communicationTemplateApi.updateTemplate(
        Number(template.id),
        apiPayload
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.ux.template.templatesFilter(filters),
        });
        toast({
          title: "Success",
          description: "Template saved as draft successfully!",
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "",
          description: error?.message || "Failed to save template as draft",
        });
      },
    }
  );
};

export const usePlaceholderGroups = () => {
  const key = QueryKeyFactory.module.ux.template.placeHolderGroups();

  return useSmartQuery(key as unknown[], () =>
    communicationTemplateApi.getTemplatePlaceHolders(true, "")
  );
};

export const usePlaceholdersByGroup = (category: string | null) => {
  const key = category
    ? QueryKeyFactory.module.ux.template.placeHolders(category)
    : ["placeholder_group", "empty"];

  return useSmartQuery(
    key as unknown[],
    () => {
      return communicationTemplateApi.getTemplatePlaceHolders(
        false,
        category ?? ""
      );
    },
    {
      enabled: !!category,
    }
  );
};

export const useCloneTemplate = (filters: TemplateFilters = {}) => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (templateId: number) => {
      if (!templateId) {
        throw new Error("Template ID is required");
      }

      return communicationTemplateApi.cloneTemplate(templateId);
    },
    {
      onSuccess: (data, variable) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.ux.template.templatesFilter(filters),
        });

        toast({
          title: "Success",
          description: `Template cloned successfully!`,
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "",
          description: error?.message || "Failed to save template as draft",
        });
      },
    }
  );
};
export const useToggleTemplateStatus = (filters: TemplateFilters = {}) => {
  const queryClient = useQueryClient();
  return useSmartMutation(
    async (data: { listId: number; isActive: boolean }) => {
      if (!data.listId) {
        throw new Error("Template ID is missing");
      }

      return communicationTemplateApi.toggleTemplateStatus(
        data.listId,
        data.isActive
      );
    },
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.ux.template.templatesFilter(filters),
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "",
          description: error?.message || "Failed to save template as draft",
        });
      },
    }
  );
};
